/**
 * Mobile buy bar: on phones the purchase button is fixed to the
 * bottom of the screen from page load. It forwards clicks to the product
 * form's own Add to Cart button (hidden on phones), so size selection — with
 * no size chosen it scrolls to the size picker — Notify Me, errors and cart
 * updates stay single-sourced. Label and disabled state mirror that button.
 */
class MobileStickyAddToCart extends HTMLElement {
  #form;
  #canonicalButton;
  #mutationObserver;
  #resizeObserver;

  connectedCallback() {
    if (this.closest('.quick-add-modal')) return;

    this.#form = this.parentElement?.querySelector('product-form-component');
    if (!this.#form) return;

    this.querySelector('[data-mobile-sticky-button]')?.addEventListener('click', this.#forwardClick);
    this.#mutationObserver = new MutationObserver(this.#bindCanonicalButton);
    this.#mutationObserver.observe(this.#form, {
      attributes: true,
      childList: true,
      subtree: true,
      characterData: true,
    });
    this.#bindCanonicalButton();

    // Reserve the bar's height at the bottom of the page so it never covers
    // the footer.
    this.#resizeObserver = new ResizeObserver(([entry]) => {
      const height = entry?.target instanceof HTMLElement ? entry.target.offsetHeight : 0;
      document.body.style.setProperty('--mobile-buy-bar-height', `${height}px`);
    });
    this.#resizeObserver.observe(this);

    this.removeAttribute('hidden');
    this.dataset.visible = 'true';
    document.body.style.setProperty('--mobile-buy-bar-height', `${this.offsetHeight}px`);
  }

  disconnectedCallback() {
    this.querySelector('[data-mobile-sticky-button]')?.removeEventListener('click', this.#forwardClick);
    this.#mutationObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    document.body.style.removeProperty('--mobile-buy-bar-height');
  }

  #bindCanonicalButton = () => {
    const button = this.#form?.querySelector('add-to-cart-component button[ref="addToCartButton"]');
    if (!(button instanceof HTMLButtonElement)) return;

    this.#canonicalButton = button;
    this.#syncButton();
  };

  #syncButton() {
    const stickyButton = this.querySelector('[data-mobile-sticky-button]');
    const stickyLabel = this.querySelector('[data-mobile-sticky-label]');
    const canonicalLabel = this.#canonicalButton?.querySelector('.add-to-cart-text__content');

    if (!(stickyButton instanceof HTMLButtonElement)) return;

    // Disabled while the cart request runs (no double taps), but the bar stays
    // solid — see the CSS. While the form button shows its "Added"
    // confirmation, show the same text.
    stickyButton.disabled = Boolean(this.#canonicalButton?.disabled);

    const added = Boolean(this.#canonicalButton?.classList.contains('atc-added'));
    const addedLabel = this.#canonicalButton?.querySelector('.add-to-cart-text--added');
    const label = added && addedLabel ? addedLabel.textContent : canonicalLabel?.textContent;
    if (stickyLabel && label) stickyLabel.textContent = label.trim();
  }

  #forwardClick = () => {
    const needsSize =
      this.#form?.dataset.requiresSizeSelection === 'true' && this.#form.dataset.sizeSelected !== 'true';

    this.#canonicalButton?.click();

    // With no size chosen the form scrolls the size picker to the middle of
    // the screen. From the bar, frame it instead with the "Select a size"
    // message just above the bar, so title, price and sizes show together —
    // without pushing the sizes under the header.
    if (needsSize) window.requestAnimationFrame(this.#frameSizePicker);
  };

  #frameSizePicker = () => {
    const picker = this.#form
      ?.closest('.shopify-section, dialog, product-card')
      ?.querySelector('variant-picker [data-size-option]');
    const message = this.#form?.querySelector('[data-size-selection-error]');
    if (!(picker instanceof HTMLElement)) return;

    const anchor = message instanceof HTMLElement && message.offsetParent ? message : picker;
    const headerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    const anchorBottom = anchor.getBoundingClientRect().bottom + window.scrollY;
    const pickerTop = picker.getBoundingClientRect().top + window.scrollY;

    const target = Math.min(
      anchorBottom - (window.innerHeight - this.offsetHeight - 24),
      pickerTop - headerHeight - 16
    );
    window.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
  };
}

if (!customElements.get('mobile-sticky-add-to-cart')) {
  customElements.define('mobile-sticky-add-to-cart', MobileStickyAddToCart);
}
